import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import OnboardingWizard from "@/components/OnboardingWizard";
import { X, ArrowLeft, ArrowRight, CheckCircle2, Circle, Sparkles } from "lucide-react";

// Every step targets REAL elements (data-testid) and navigates to the page/tab first.
// `setup` links the step to the real checklist item (already configured -> marked complete).
export const TOUR_STEPS = [
  { k: "welcome", route: "/", target: null },
  { k: "dashboard", route: "/", target: "dash-kpi-grid" },
  { k: "company", route: "/impostazioni?tab=azienda", target: "company-card", setup: "company", admin: true },
  { k: "branding", route: "/impostazioni?tab=branding", target: "branding-card", setup: "branding", admin: true },
  { k: "ai", route: "/impostazioni?tab=ai", target: "ai-behaviour-card", setup: "ai", admin: true },
  { k: "catalog", route: "/impostazioni?tab=catalogo", target: "catalog-tab", setup: "catalog", admin: true, wizard: true },
  { k: "quotes", route: "/impostazioni?tab=listino", target: "quotes-mode-card", setup: "quotes_mode", admin: true },
  { k: "whatsapp", route: "/impostazioni?tab=whatsapp", target: "whatsapp-settings", setup: "whatsapp", admin: true },
  { k: "users", route: "/impostazioni?tab=utenti", target: "users-card", setup: "users", admin: true },
  { k: "simulator", route: "/simulatore", target: "simulator-panel", setup: "simulator", admin: true },
  { k: "inbox", route: "/inbox", target: "inbox-page" },
  { k: "customers", route: "/pratiche", target: "pratiche-page" },
  { k: "quotes_send", route: "/preventivi", target: "preventivi-page" },
  { k: "visits", route: "/sopralluoghi", target: "sopralluoghi-page" },
  { k: "finish", route: "/", target: null },
];

const PAD = 8;
const isMobile = () => window.innerWidth < 640;

function useTargetRect(testid, active) {
  const [rect, setRect] = useState(null);
  const locate = useCallback(() => {
    if (!active || !testid) { setRect(null); return false; }
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (!el) { setRect(null); return false; }
    const r = el.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: "center", behavior: "auto" });
    setRect(el.getBoundingClientRect());
    return true;
  }, [testid, active]);
  useLayoutEffect(() => {
    if (!active) return undefined;
    let tries = 0;
    const tm = setInterval(() => { if (locate() || ++tries > 40) clearInterval(tm); }, 100);
    return () => clearInterval(tm);
  }, [locate, active]);
  useEffect(() => {
    if (!active) return undefined;
    const on = () => locate();
    window.addEventListener("resize", on); window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [locate, active]);
  return rect;
}

function Mask({ box }) {
  // 4 rectangles around the hole: the target itself stays clickable (overlay never covers it).
  // Clicking the backdrop never closes the tour (only X / Esc / "Skip for now").
  if (!box) return <div className="absolute inset-0 bg-black/55 pointer-events-auto" />;
  const W = window.innerWidth, H = window.innerHeight;
  const r = [
    { top: 0, left: 0, width: W, height: Math.max(box.top, 0) },
    { top: box.top + box.height, left: 0, width: W, height: Math.max(H - box.top - box.height, 0) },
    { top: box.top, left: 0, width: Math.max(box.left, 0), height: box.height },
    { top: box.top, left: box.left + box.width, width: Math.max(W - box.left - box.width, 0), height: box.height },
  ];
  return <>{r.map((s, i) => <div key={i} className="absolute bg-black/55 pointer-events-auto" style={s} />)}
    <div className="absolute rounded-xl ring-4 ring-sky-400 pointer-events-none transition-all duration-200" style={box} /></>;
}

export default function GuidedTour() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const admin = user?.role === "TENANT_ADMIN";
  const tenantUser = !!user && !!user.tenant_id && user.role !== "PLATFORM_OWNER";
  const steps = TOUR_STEPS.filter((s) => admin || !s.admin);
  const [phase, setPhase] = useState("hidden"); // hidden | intro | resume | running
  const [idx, setIdx] = useState(0);
  const [saved, setSaved] = useState(null);
  const [setup, setSetup] = useState(null);
  const [wizard, setWizard] = useState(false);
  const tipRef = useRef(null);
  const step = steps[idx] || steps[0];
  const rect = useTargetRect(step.target, phase === "running" && !wizard);

  const save = useCallback((body) => http.put("/onboarding/tour", body).then((r) => { setSaved(r.data); return r.data; }).catch(() => null), []);
  const loadSetup = useCallback(() => http.get("/onboarding/setup-status").then((r) => setSetup(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (!tenantUser) return;
    http.get("/onboarding/tour").then((r) => {
      setSaved(r.data);
      if (r.data.status === "not_started" && admin) setPhase("intro");
      else if (r.data.status === "in_progress") { setIdx(Math.min(r.data.step || 0, steps.length - 1)); setPhase("resume"); }
    }).catch(() => {});
  }, [tenantUser, admin]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = useCallback((s) => {
    const target = steps[s];
    if (!target) return;
    const cur = loc.pathname + loc.search;
    if (cur !== target.route) nav(target.route);
  }, [steps, loc.pathname, loc.search, nav]);

  const begin = (s = 0) => { setIdx(s); setPhase("running"); loadSetup(); save({ status: "in_progress", step: s }); go(s); };
  const move = (s) => { setIdx(s); save({ step: s }); go(s); };
  const close = () => { setPhase("hidden"); setWizard(false); };
  const skip = () => { save({ status: "skipped", step: idx }); close(); window.dispatchEvent(new Event("nm-tour-changed")); };
  const complete = () => { save({ status: "completed", step: steps.length - 1 }); close(); window.dispatchEvent(new Event("nm-tour-changed")); };

  useEffect(() => {
    const restart = () => { setIdx(0); setPhase("intro"); };
    const resume = () => { begin(Math.min(saved?.step || 0, steps.length - 1)); };
    window.addEventListener("nm-start-tour", restart);
    window.addEventListener("nm-resume-tour", resume);
    return () => { window.removeEventListener("nm-start-tour", restart); window.removeEventListener("nm-resume-tour", resume); };
  }); // intentionally re-bound each render to see fresh `saved`

  useEffect(() => { if (phase === "running" && !wizard) tipRef.current?.focus(); }, [phase, idx, wizard]);
  useEffect(() => {
    if (phase !== "running") return undefined;
    const onEsc = (e) => { if (e.key === "Escape" && !wizard) close(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }); // re-bound each render (close/wizard are fresh closures)
  useEffect(() => { if (phase === "running" && step.k === "finish") loadSetup(); }, [phase, step.k, loadSetup]);

  const onKey = (e) => {
    if (e.key === "Escape") return; // handled at document level
    if (e.key === "ArrowRight") { e.preventDefault(); idx < steps.length - 1 ? move(idx + 1) : complete(); }
    else if (e.key === "ArrowLeft" && idx > 0) { e.preventDefault(); move(idx - 1); }
    else if (e.key === "Tab" && tipRef.current) {
      const f = tipRef.current.querySelectorAll("button:not([disabled]),a[href],input");
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  if (phase === "hidden" || !user) return null;

  if (phase === "intro" || phase === "resume") {
    const resume = phase === "resume";
    return (
      <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4" data-testid={resume ? "tour-resume" : "tour-intro"} role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-3"><Sparkles className="w-6 h-6" /></div>
          <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{resume ? t("tour.resume_title") : t("tour.welcome")}</h2>
          <p className="text-sm text-slate-500 mt-2 mb-5">{resume ? t("tour.resume_body", { n: idx + 1, total: steps.length }) : t("tour.intro")}</p>
          <div className="flex gap-2">
            <button data-testid="tour-skip" onClick={skip} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50">{t("tour.skip_now")}</button>
            <button data-testid="tour-start" autoFocus onClick={() => begin(resume ? idx : 0)} className="flex-1 py-2.5 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700">{resume ? t("tour.resume_btn") : t("tour.start")}</button>
          </div>
          {resume && <button data-testid="tour-restart" onClick={() => begin(0)} className="mt-3 text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2">{t("tour.restart_btn")}</button>}
        </div>
      </div>
    );
  }

  // running
  const last = idx === steps.length - 1;
  const setupItem = step.setup && setup?.items?.find((i) => i.key === step.setup);
  const box = rect ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 } : null;
  const mobile = isMobile();
  let tipStyle;
  if (mobile) tipStyle = { left: 12, right: 12, bottom: 12 };
  else if (!box) tipStyle = { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  else {
    const below = box.top + box.height + 12, spaceBelow = window.innerHeight - below;
    const right = box.left + box.width + 12;
    if (spaceBelow > 260) tipStyle = { top: below, left: Math.min(Math.max(box.left, 12), window.innerWidth - 372) };
    else if (box.top > 262) tipStyle = { top: box.top - 12 - 250, left: Math.min(Math.max(box.left, 12), window.innerWidth - 372) };
    else if (right + 372 < window.innerWidth) tipStyle = { top: Math.max(12, box.top), left: right };
    else if (box.left > 384) tipStyle = { top: Math.max(12, box.top), left: box.left - 372 };
    else tipStyle = { top: 12, right: 12 };
  }

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none" data-testid="tour-overlay">
      <Mask box={box} />
      <div ref={tipRef} tabIndex={-1} onKeyDown={onKey} role="dialog" aria-modal="true" aria-labelledby="tour-title"
        className={`absolute pointer-events-auto bg-white rounded-2xl shadow-2xl p-5 outline-none ${mobile ? "" : "w-[360px]"} ${step.k === "finish" || step.k === "welcome" ? "max-w-md" : ""}`}
        style={tipStyle} data-testid="tour-tooltip" data-step={step.k}>
        <button onClick={close} aria-label={t("tour.close")} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600" data-testid="tour-close"><X className="w-4 h-4" /></button>
        <div className="text-xs font-semibold text-sky-600 mb-1" data-testid="tour-counter">{t("tour.step_of", { n: idx + 1, total: steps.length })}</div>
        <h3 id="tour-title" className="font-heading text-lg font-extrabold text-[#0F172A] pr-6">{t(`tour.s_${step.k}_title`)}</h3>
        <p className="text-sm text-slate-700 mt-1.5" data-testid="tour-body">{t(`tour.s_${step.k}_body`)}</p>
        <p className="text-xs text-slate-500 mt-2"><span className="font-semibold text-slate-600">{t("tour.why_label")}:</span> {t(`tour.s_${step.k}_why`)}</p>
        {setupItem && (
          <div className={`mt-2 text-xs font-semibold flex items-center gap-1.5 ${setupItem.done ? "text-emerald-600" : "text-amber-600"}`} data-testid="tour-step-status">
            {setupItem.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
            {setupItem.done ? t("tour.already_done") : t("tour.todo")}
          </div>
        )}
        {step.wizard && admin && (
          <button onClick={() => setWizard(true)} className="mt-2 text-xs font-semibold text-sky-700 underline underline-offset-2" data-testid="tour-open-catalog-wizard">{t("tour.open_catalog_wizard")}</button>
        )}
        {step.k === "finish" && setup && (
          <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1" data-testid="tour-summary">
            <div className="text-xs font-semibold text-slate-600">{t("tour.summary_title", { p: setup.percent })}</div>
            {setup.items.map((i) => (
              <div key={i.key} className="flex items-center gap-2 text-xs">
                {i.done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
                <span className={i.done ? "text-slate-400 line-through" : "text-slate-700"}>{t(`checklist.item_${i.key}`)}{!i.required && ` (${t("checklist.optional")})`}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-4">
          <button data-testid="tour-skip-run" onClick={skip} className="text-xs text-slate-400 hover:text-slate-600 font-medium">{t("tour.skip_now")}</button>
          <div className="flex gap-2">
            <button data-testid="tour-back" disabled={idx === 0} onClick={() => move(idx - 1)} className="text-xs font-semibold border border-slate-200 rounded-lg px-3 py-2 text-slate-600 disabled:opacity-40 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> {t("tour.back")}</button>
            {last && setup && !setup.all_required_done ? (
              <button data-testid="tour-finish-next" onClick={() => { complete(); if (setup.next) nav(setup.next.to); }} className="text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-lg px-3 py-2 flex items-center gap-1">{t("tour.continue_config")} <ArrowRight className="w-3.5 h-3.5" /></button>
            ) : (
              <button data-testid="tour-next" onClick={() => (last ? complete() : move(idx + 1))} className="text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white rounded-lg px-3 py-2 flex items-center gap-1">
                {last ? t("tour.done") : t("tour.next")} {!last && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>
      </div>
      {wizard && <div className="pointer-events-auto"><OnboardingWizard onClose={() => { setWizard(false); loadSetup(); }} onDone={loadSetup} /></div>}
    </div>
  );
}
